import { defineStore } from 'pinia';
import { StorageSerializers, useLocalStorage } from '@vueuse/core';
import { useBackendFetch } from '@/fetch';
import type { AccountOnboarding, GoalPlan, LoginData, Profile } from '@/types';
import { debugLog, debugWarn } from '@/utils/logger';

// Local mutation sequences protect against older in-flight response snapshots.
const valueTimestamps: Record<string, number> = {};
const PROFILE_RECONCILE_INTERVAL_MS = 30 * 1000;
let lastProfileReconcileAt = 0;
let lastProfileReconcileScope = '';
let answerTrackingScope = '';
let savePointsMetadataTimer: number | null = null;
const categoryAnswerRequests: Record<number, Promise<Record<string, string>>> = {};
const outboxFlushRequests = new Map<string, Promise<void>>();
const profileReconcileRequests = new Map<string, Promise<boolean>>();
const answerWriteRequests = new Map<string, Promise<any>>();
const outboxMemoryFallback = new Map<string, OutboxEntry[]>();

// Improve ("Vill förbättra"/"Jag är nöjd") choices made locally, per category.
// Used to protect them from being overwritten by stale server snapshots.
const improveTimestamps: Record<string, number> = {};

// Monotonic counter for answer saves. When the user switches answers quickly,
// the response of an OLDER save can arrive after a newer save was dispatched;
// its server-computed factors (the 1-5 risk values) are then stale and must
// not overwrite the state - that was the visible "bounce" on the risk scale.
let profileSaveSequence = 0;
// Acknowledgements invalidate GET snapshots, but are not new save dispatches.
let profileSnapshotSequence = 0;

type OutboxEntry = {
  kind?: 'value' | 'improve';
  name: string;
  value: string;
  client_request_id?: string;
  category?: number;
  ts: number;
};

type ProfileRequestContext = {
  profileId: number | null;
  userId: number;
  token: string;
};

function requestScope(context: ProfileRequestContext) {
  return JSON.stringify([context.userId, context.profileId, context.token]);
}

function answerField(entry: OutboxEntry) {
  return entry.kind === 'improve' ? `improve:${entry.category}` : `value:${entry.name}`;
}

function sameOutboxEntry(left: OutboxEntry, right: OutboxEntry) {
  return answerField(left) === answerField(right) &&
    left.client_request_id === right.client_request_id &&
    left.ts === right.ts && left.value === right.value;
}

function answerDeliveryPaused(background = false) {
  return (typeof navigator !== 'undefined' && navigator.onLine === false) ||
    (background && typeof document !== 'undefined' && document.visibilityState === 'hidden');
}

function outboxStorageKey(context: ProfileRequestContext) {
  return context.profileId && context.userId
    ? `answer-outbox:user:${context.userId}:profile:${context.profileId}`
    : null;
}

function readScopedAnswerOutbox(context: ProfileRequestContext): OutboxEntry[] {
  try {
    const key = outboxStorageKey(context);
    if (!key) return [];
    const fallback = outboxMemoryFallback.get(key);
    if (fallback) return fallback;
    // Legacy queues used globally unique profile ids. Never read an unscoped queue.
    const raw = localStorage.getItem(key) ?? localStorage.getItem(`answer-outbox:profile:${context.profileId}`);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    debugWarn('Could not read answer outbox', error);
    return [];
  }
}

function countPendingAnswerSaves(context: ProfileRequestContext): number {
  const prefix = `${outboxStorageKey(context)}:`;
  const fields = new Set(readScopedAnswerOutbox(context).map(answerField));
  for (const key of answerWriteRequests.keys()) {
    if (key.startsWith(prefix)) fields.add(key.slice(prefix.length));
  }
  return fields.size;
}

type SavePoints = Record<string, any> & {
  profile_id?: number;
};

type QuestionFlowResume = {
  route: string;
  updated_at?: string | null;
};

function clearValueTimestamps() {
  for (const key of Object.keys(valueTimestamps)) {
    delete valueTimestamps[key];
  }
  for (const key of Object.keys(improveTimestamps)) {
    delete improveTimestamps[key];
  }
}

function createClientRequestId(profileId: number, name: string, timestamp: number) {
  const random = Math.random().toString(36).slice(2, 10);
  return `${profileId}:${name}:${timestamp}:${random}`.slice(0, 120);
}

export const useStore = defineStore('main', {
  state() {
    return {
      loggedIn: useLocalStorage('logged-in', false),
      userId: useLocalStorage('user-id', 0),
      firstName: useLocalStorage('user-first-name', ''),
      lastName: useLocalStorage('user-last-name', ''),
      company: useLocalStorage('user-company', ''),
      email: useLocalStorage('user-email', ''),
      token: useLocalStorage('user-token', ''),
      sex: useLocalStorage('user-sex', 'U'),
      onboarding: useLocalStorage<AccountOnboarding>('account-onboarding', {
        status: 'complete',
        organization: null,
        sections: [],
        section_id: null,
        missing: { birth_date: false, section: false },
      }, { serializer: StorageSerializers.object }),
      profile: useLocalStorage('user-profile', null, {
        serializer: StorageSerializers.object,
      }),
      savePoints: useLocalStorage<SavePoints | null>('save-points', null, {
        serializer: StorageSerializers.object,
      }),
      profiles: useLocalStorage<{ id: number; created_at: string }[]>(
        'user-profiles',
        [],
      ),
      feedbackQuestions: useLocalStorage<
        {
          name: string;
          text: string;
          options: { text: string; value: string }[];
        }[]
      >('feedback-questions', []),
      answerTexts: useLocalStorage<Record<number, Array<{ value: string; text: string }>>>('answer-texts', {}),
      categoryAnswers: useLocalStorage<Record<number, Record<string, string>>>('category-answers', {}),
      // Session management
      lastActivity: useLocalStorage('last-activity', 0),
      sessionTimeout: useLocalStorage('session-timeout', 4 * 60 * 60 * 1000), // 4 hours in milliseconds
      sessionWarningShown: useLocalStorage('session-warning-shown', false),
      latestFinishedUnfinishedAiPlans: null as null | {
        profile_id: number;
        profile_created_at?: string | null;
        active_count: number;
        proposed_count: number;
        total_count: number;
      },
      answerSyncVersion: 0,
    };
  },
  getters: {
    pendingAnswerSaves(): number {
      // Outbox writes and request completion invalidate this localStorage-backed count.
      void this.answerSyncVersion;
      const id = Number(this.profile?.id);
      return countPendingAnswerSaves({
        profileId: Number.isFinite(id) && id > 0 ? id : null,
        userId: this.userId,
        token: this.token,
      });
    },
  },
  actions: {
    setUserInfo(email: string) {
      const users: Record<string, string> = {
        'stefan.vonbelow@nobina.se': 'Stefan von Below',
        'robert.borger@nobina.se': 'Robert Borger',
        'sarah.elifari@nobina.se': 'Sarah Elifari',
        'susanna.lejeborg@nonina.se': 'Susanna Lejeborg',
        'eva.palsson@nobina.se': 'Eva Pålsson',
        'carolina.granstrom@nobina.se': 'Carolina Granström',
      };

      if (email.length > 0 && email in users) {
        this.firstName = users[email].split(' ')[0];
        this.lastName = users[email].split(' ')[1];
        this.company = 'Nobina';
        this.email = email;
      } else {
        this.firstName = 'Testare';
        this.company = 'Testföretag AB';
        this.email = 'test@foretag.se';
      }
    },
    login(data: LoginData) {
      this.loggedIn = true;
      this.token = data.token;
      this.userId = data.user.id;
      this.firstName = data.user.first_name;
      this.lastName = data.user.last_name;
      this.email = data.user.email;
      this.company = data.user.company;
      this.profile = data.user.active_profile;
      this.sex = data.user.sex;
      this.onboarding = data.onboarding || {
        status: 'complete',
        organization: null,
        sections: [],
        section_id: null,
        missing: { birth_date: false, section: false },
      };
      this.profiles = data.user.finished_profiles;
      this.normalizeProfileScopedState();
      this.feedbackQuestions = [];
      // Initialize session tracking
      this.updateLastActivity();
      this.sessionWarningShown = false;
      
      // Trigger session management (will be handled by the composable)
      debugLog('🎉 User logged in - session management will start automatically');
    },
    async logout(skipRemote = false) {
      const logoutToken = this.token;
      // Clear locally before any await so route guards cannot see an expired login.
      this.loggedIn = false;
      this.userId = 0;
      this.firstName = '';
      this.lastName = '';
      this.company = '';
      this.email = '';
      this.token = '';
      this.profile = null;
      this.savePoints = null;
      this.profiles = [];
      this.feedbackQuestions = [];
      this.latestFinishedUnfinishedAiPlans = null;
      this.onboarding = {
        status: 'complete',
        organization: null,
        sections: [],
        section_id: null,
        missing: { birth_date: false, section: false },
      };
      // Clear session tracking
      this.lastActivity = 0;
      this.sessionWarningShown = false;
      
      // Clear all localStorage data
      this.clearAllStorage();

      if (logoutToken && !skipRemote) {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 8000);
        try {
          await fetch(`${import.meta.env.APP_BACKEND_API_ENDPOINT}/logout`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${logoutToken}`,
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            signal: controller.signal,
          });
        } catch (error) {
          debugWarn('Backend logout unavailable; local session is already cleared.', error);
        } finally {
          clearTimeout(timeout);
        }
      }
    },

    clearAllStorage() {
      // Clear all localStorage items related to the app
      const keysToRemove = [
        'logged-in',
        'user-id',
        'user-first-name',
        'user-last-name',
        'user-company',
        'user-email',
        'user-token',
        'user-sex',
        'account-onboarding',
        'user-profile',
        'save-points',
        'user-profiles',
        'feedback-questions',
        'answer-texts',
        'category-answers',
        'last-activity',
        'session-timeout',
        'session-warning-shown'
      ];

      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
      });

      Object.keys(localStorage)
        .filter(key =>
          key.startsWith('save-points:profile:') ||
          key.startsWith('sectionResults:profile:') ||
          key.startsWith('question-flow:profile:'),
        )
        .forEach(key => localStorage.removeItem(key));

      // Clear sessionStorage
      sessionStorage.clear();

      // Clear cookies
      document.cookie.split(';').forEach(cookie => {
        const eqPos = cookie.indexOf('=');
        const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
        if (name) {
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
        }
      });
    },
    getActiveProfileId(): number | null {
      const id = Number(this.profile?.id);
      return Number.isFinite(id) && id > 0 ? id : null;
    },
    getProfileRequestContext(): ProfileRequestContext {
      const context = { profileId: this.getActiveProfileId(), userId: this.userId, token: this.token };
      const scope = requestScope(context);
      if (answerTrackingScope !== scope) {
        clearValueTimestamps();
        answerTrackingScope = scope;
        profileSaveSequence++;
        profileSnapshotSequence++;
      }
      return context;
    },
    isProfileRequestCurrent(context: ProfileRequestContext) {
      return this.loggedIn && this.token === context.token && this.userId === context.userId &&
        this.getActiveProfileId() === context.profileId;
    },
    profileRequestOptions(context: ProfileRequestContext) {
      return {
        timeout: 8000,
        // Guard before shared fetch hooks see a stale response (including an old 401).
        fetch: async (input: RequestInfo | URL, init?: RequestInit) => {
          if (!this.isProfileRequestCurrent(context)) throw new Error('Profile session changed');
          const response = await fetch(input, init);
          if (!this.isProfileRequestCurrent(context)) throw new Error('Profile session changed');
          if (response.status === 401) void this.logout(true);
          return response;
        },
      };
    },
    profileScopedStorageKey(baseKey: string, profileId = this.getActiveProfileId()) {
      return profileId ? `${baseKey}:profile:${profileId}` : baseKey;
    },
    profileScopedSessionKey(baseKey: string, profileId = this.getActiveProfileId()) {
      return profileId ? `${baseKey}:profile:${profileId}` : baseKey;
    },
    readProfileScopedJson<T>(baseKey: string, fallback: T, profileId = this.getActiveProfileId()): T {
      if (!profileId) return fallback;

      try {
        const raw = localStorage.getItem(this.profileScopedStorageKey(baseKey, profileId));
        return raw ? JSON.parse(raw) as T : fallback;
      } catch {
        return fallback;
      }
    },
    writeProfileScopedJson(baseKey: string, value: unknown, profileId = this.getActiveProfileId()) {
      if (!profileId) return;
      localStorage.setItem(this.profileScopedStorageKey(baseKey, profileId), JSON.stringify(value));
    },
    isQuestionFlowRoute(route: string | null | undefined) {
      if (!route) return false;
      return [
        '/evaluate',
        '/questions/',
        '/goals',
        '/plan',
      ].some(prefix => route === prefix || route.startsWith(prefix));
    },
    recordQuestionFlowRoute(route: string) {
      const profileId = this.getActiveProfileId();
      if (!profileId || !this.isQuestionFlowRoute(route)) return;

      this.writeProfileScopedJson(
        'question-flow-resume',
        {
          route,
          updated_at: new Date().toISOString(),
        },
        profileId,
      );
      this.scheduleSavePointsMetadataSync();
    },
    getQuestionFlowResume(profileId = this.getActiveProfileId()): QuestionFlowResume | null {
      return this.readProfileScopedJson<QuestionFlowResume | null>(
        'question-flow-resume',
        null,
        profileId,
      );
    },
    getQuestionFlowResumeRoute(profileId = this.getActiveProfileId()) {
      const resume = this.getQuestionFlowResume(profileId);
      return this.isQuestionFlowRoute(resume?.route) ? resume?.route || null : null;
    },
    async getUserMetadata(namespace: string, key: string) {
      if (!this.loggedIn || !this.token) {
        return null;
      }

      try {
        const { data, statusCode } = await useBackendFetch(
          `/metadata?scope=user&namespace=${encodeURIComponent(namespace)}&key=${encodeURIComponent(key)}`,
        )
          .get()
          .json();

        if (statusCode.value === 401) {
          this.logout();
          return null;
        }

        const row = Array.isArray(data.value?.metadata) ? data.value.metadata[0] : null;
        return row?.value ?? null;
      } catch {
        return null;
      }
    },
    async hasSeenUserGuide(key: string, localFallbackKey?: string) {
      const scopedFallbackKey = localFallbackKey && this.userId
        ? `${localFallbackKey}:user:${this.userId}`
        : localFallbackKey;
      const metadata = await this.getUserMetadata('guides', key);
      if (metadata?.seen === true) {
        if (scopedFallbackKey) localStorage.setItem(scopedFallbackKey, '1');
        return true;
      }

      if (scopedFallbackKey && localStorage.getItem(scopedFallbackKey)) {
        return true;
      }

      if (localFallbackKey && localStorage.getItem(localFallbackKey)) {
        await this.markUserGuideSeen(key, localFallbackKey);
        return true;
      }

      return false;
    },
    async markUserGuideSeen(key: string, localFallbackKey?: string) {
      const scopedFallbackKey = localFallbackKey && this.userId
        ? `${localFallbackKey}:user:${this.userId}`
        : localFallbackKey;
      if (scopedFallbackKey) {
        localStorage.setItem(scopedFallbackKey, '1');
      }

      if (!this.loggedIn || !this.token) {
        return;
      }

      try {
        await useBackendFetch('/metadata')
          .post({
            scope: 'user',
            namespace: 'guides',
            key,
            schema_version: '2026-05-22',
            value: {
              seen: true,
              seen_at: new Date().toISOString(),
            },
          })
          .json();
      } catch {
        // Browser fallback is enough if metadata sync is temporarily unavailable.
      }
    },
    finalFeedbackSubmittedFallbackKey() {
      return this.userId ? `bwell.final-feedback-submitted:user:${this.userId}` : 'bwell.final-feedback-submitted';
    },
    async hasSubmittedFinalFeedback() {
      if (this.loggedIn && this.token) {
        try {
          const { data, statusCode } = await useBackendFetch('/feedback/status')
            .get()
            .json();

          if (statusCode.value === 401) {
            this.logout();
            return false;
          }

          if (data.value?.submitted === true) {
            localStorage.setItem(this.finalFeedbackSubmittedFallbackKey(), '1');
            return true;
          }
        } catch {
          // Fall through to metadata/local fallback if the status endpoint is unavailable.
        }
      }

      const metadata = await this.getUserMetadata('feedback', 'final_submitted_v1');
      if (metadata?.submitted === true) {
        localStorage.setItem(this.finalFeedbackSubmittedFallbackKey(), '1');
        return true;
      }

      return localStorage.getItem(this.finalFeedbackSubmittedFallbackKey()) === '1';
    },
    async markFinalFeedbackSubmitted() {
      localStorage.setItem(this.finalFeedbackSubmittedFallbackKey(), '1');

      if (!this.loggedIn || !this.token) {
        return;
      }

      try {
        await useBackendFetch('/metadata')
          .post({
            scope: 'user',
            namespace: 'feedback',
            key: 'final_submitted_v1',
            schema_version: '2026-06-03',
            value: {
              submitted: true,
              submitted_at: new Date().toISOString(),
            },
          })
          .json();
      } catch {
        // Local fallback keeps the icon hidden in this browser if metadata sync fails.
      }
    },
    normalizeProfileScopedState() {
      const profileId = this.getActiveProfileId();
      if (!profileId) {
        this.savePoints = null;
        return;
      }

      const scopedSavePoints = this.readProfileScopedJson<SavePoints | null>('save-points', null, profileId);
      if (scopedSavePoints) {
        this.savePoints = {
          ...scopedSavePoints,
          profile_id: profileId,
        };
        return;
      }

      if (this.savePoints?.profile_id === profileId) {
        this.writeProfileScopedJson('save-points', this.savePoints, profileId);
        return;
      }

      this.savePoints = { profile_id: profileId };
      this.writeProfileScopedJson('save-points', this.savePoints, profileId);
    },
    patchSavePoints(patch: SavePoints) {
      const profileId = this.getActiveProfileId();
      const current = this.savePoints && typeof this.savePoints === 'object' ? this.savePoints : {};

      this.savePoints = {
        ...current,
        ...patch,
        ...(profileId ? { profile_id: profileId } : {}),
      };

      if (profileId) {
        this.writeProfileScopedJson('save-points', this.savePoints, profileId);
        this.scheduleSavePointsMetadataSync();
      }
    },
    setWorkSavePoint(index: number) {
      this.patchSavePoints({ work: index });
    },
    patchLifeSavePoint(patch: Record<string, any>) {
      const currentLife = this.savePoints?.life && typeof this.savePoints.life === 'object'
        ? this.savePoints.life
        : {};

      this.patchSavePoints({
        life: {
          ...currentLife,
          ...patch,
        },
      });
    },
    addVisitedLifeSection(sectionId: string | number) {
      const currentLife = this.savePoints?.life && typeof this.savePoints.life === 'object'
        ? this.savePoints.life
        : {};
      const visitedSections = Array.isArray(currentLife.visitedSections)
        ? [...currentLife.visitedSections]
        : [];

      if (!visitedSections.includes(sectionId)) {
        visitedSections.push(sectionId);
      }

      this.patchLifeSavePoint({ visitedSections });
    },
    clearProfileScopedSavePoints(profileId = this.getActiveProfileId()) {
      if (profileId) {
        localStorage.removeItem(this.profileScopedStorageKey('save-points', profileId));
        sessionStorage.removeItem(this.profileScopedSessionKey('sectionIdx', profileId));
      }
      this.savePoints = null;
    },
    scheduleSavePointsMetadataSync() {
      if (savePointsMetadataTimer) {
        clearTimeout(savePointsMetadataTimer);
      }

      savePointsMetadataTimer = setTimeout(() => {
        this.saveProfileFlowMetadata();
      }, 600);
    },
    async saveProfileFlowMetadata() {
      const profileId = this.getActiveProfileId();
      if (!profileId || !this.loggedIn || !this.token || !this.savePoints) {
        return;
      }

      try {
        const cachedResume = this.getQuestionFlowResume(profileId);
        const currentRoute = `${window.location.pathname}${window.location.search}`;
        const resumeRoute = this.isQuestionFlowRoute(cachedResume?.route)
          ? cachedResume?.route
          : currentRoute;
        await useBackendFetch('/metadata')
          .post({
            profile_id: profileId,
            scope: 'profile',
            namespace: 'question_flow',
            key: 'save_points',
            schema_version: '2026-05-21',
            value: {
              save_points: this.savePoints,
              resume_route: resumeRoute,
              active_profile_id: profileId,
              updated_at: cachedResume?.updated_at ?? new Date().toISOString(),
            },
          })
          .json();
      } catch {
        // Local profile-scoped storage remains the fallback when metadata sync fails.
      }
    },
    async loadProfileFlowMetadata() {
      const profileId = this.getActiveProfileId();
      if (!profileId || !this.loggedIn || !this.token) {
        return;
      }

      try {
        const { data, statusCode } = await useBackendFetch(
          `/metadata?profile_id=${profileId}&scope=profile&namespace=question_flow&key=save_points`,
        )
          .get()
          .json();

        if (statusCode.value === 401) {
          this.logout();
          return;
        }

        const metadata = data.value?.metadata;
        const metadataValue = Array.isArray(metadata) ? metadata[0]?.value : null;
        const flowState = metadataValue?.save_points ?? null;
        if (flowState && typeof flowState === 'object') {
          this.savePoints = {
            ...flowState,
            profile_id: profileId,
          };
          this.writeProfileScopedJson('save-points', this.savePoints, profileId);
        }

        if (metadataValue?.resume_route && typeof metadataValue.resume_route === 'string') {
          this.writeProfileScopedJson(
            'question-flow-resume',
            {
              route: metadataValue.resume_route,
              updated_at: metadataValue.updated_at ?? null,
            },
            profileId,
          );
        }
      } catch {
        // Keep local profile-scoped fallback.
      }
    },
    async reconcileCurrentProfile(force = false) {
      if (!this.loggedIn || !this.token || answerDeliveryPaused()) {
        return false;
      }

      const context = this.getProfileRequestContext();
      const scope = requestScope(context);
      const existing = profileReconcileRequests.get(scope);
      if (existing) return existing;
      if (this.pendingAnswerSaves > 0) return false;

      const now = Date.now();
      if (!force && lastProfileReconcileScope === scope &&
        now - lastProfileReconcileAt < PROFILE_RECONCILE_INTERVAL_MS) {
        return true;
      }

      const localProfileId = context.profileId;
      const requestSnapshotSequence = profileSnapshotSequence;
      const request = (async () => {
        try {
          const { data, statusCode } = await useBackendFetch('/profile/current', this.profileRequestOptions(context))
            .get()
            .json();

          if (!this.isProfileRequestCurrent(context)) return false;
          if (statusCode.value === 401) {
            this.logout(true);
            return false;
          }

          if (!statusCode.value || statusCode.value < 200 || statusCode.value >= 300 ||
            !data.value || !Object.prototype.hasOwnProperty.call(data.value, 'active_profile')) {
            return false;
          }

          // A snapshot fetched before a local save/ack cannot safely describe its result.
          if (requestSnapshotSequence !== profileSnapshotSequence) return false;

          const payload = data.value;
          const activeProfile = payload.active_profile as Profile | null;
          const remoteProfileId = activeProfile?.id ? Number(activeProfile.id) : null;
          if (activeProfile !== null && (!remoteProfileId || !Number.isFinite(remoteProfileId))) return false;
          if (remoteProfileId !== localProfileId && this.pendingAnswerSaves > 0) return false;

          this.profiles = Array.isArray(payload.finished_profiles)
            ? payload.finished_profiles
            : [];
          this.latestFinishedUnfinishedAiPlans = payload.latest_finished_unfinished_ai_plans ?? null;

          if (remoteProfileId !== localProfileId) {
            clearValueTimestamps();
            this.profile = activeProfile;
            this.normalizeProfileScopedState();
          } else if (activeProfile) {
            this.applyServerProfileSnapshot(activeProfile);
          } else {
            this.profile = null;
          }

          // Same-profile refresh must not move the current question or restore metadata.
          lastProfileReconcileScope = scope;
          lastProfileReconcileAt = Date.now();
          return true;
        } catch {
          debugWarn('Could not refresh current profile.');
          return false;
        }
      })();
      profileReconcileRequests.set(scope, request);
      try {
        return await request;
      } finally {
        if (profileReconcileRequests.get(scope) === request) profileReconcileRequests.delete(scope);
      }
    },
    /**
     * Apply a server profile snapshot on top of the local profile without
     * losing local edits: answer values the user changed on this device
     * (still queued in the outbox) and locally chosen
     * improve flags win over the server copy. Everything else (server-side
     * computed factors, texts, revision counters) comes from the server.
     */
    applyServerProfileSnapshot(remoteProfile: any) {
      const local = this.profile;

      if (!local || Number(local.id) !== Number(remoteProfile?.id)) {
        return;
      }

      const outbox = this.readAnswerOutbox();

      // Only pending edits win. Acknowledged fields may accept remote changes.
      const mergedValues: Record<string, any> = { ...(remoteProfile.values || {}) };
      for (const entry of outbox) {
        if ((entry.kind ?? 'value') === 'value') mergedValues[entry.name] = entry.value;
      }

      // Factors: server-computed, but locally chosen improve flags win.
      const mergedFactors: Record<string, any> = { ...(remoteProfile.factors || {}) };
      const localFactors: Record<string, any> = local.factors || {};
      for (const entry of outbox) {
        if (entry.kind === 'improve' && entry.category !== undefined) {
          const categoryId = entry.category;
          mergedFactors[categoryId] = {
            ...localFactors[categoryId],
            ...mergedFactors[categoryId],
            improve: entry.value,
          };
        }
      }

      this.profile = {
        ...remoteProfile,
        values: mergedValues,
        factors: mergedFactors,
      };
    },
    hasAnyValues() {
      if (this.profile == null || this.profile?.values == null) {
        return false;
      }

      return Object.values(this.profile?.values).length > 0;
    },
    getProfileValue(name: string) {
      return this.profile?.values?.[name] ?? null;
    },
    getProfileFactor(category: number) {
      return this.profile?.factors?.[category] ?? null;
    },
    // New API method for saving individual goal dates without overwriting
    async saveGoalDate(goalCategoryId: number, date: string, factors: any[] = []) {
      debugLog(`🎯 Saving goal date: category ${goalCategoryId} → date ${date}`);
      
      if (!this.profile?.id) {
        debugWarn('No active profile to save to');
        this.logout();
        return { success: false, error: 'No active profile' };
      }

      try {
        const { data, statusCode } = await useBackendFetch(
          `/profile/${this.profile.id}/goal/${goalCategoryId}/date`,
        )
          .post({ date, factors })
          .json();

        // Handle authentication errors
        if (statusCode?.value === 401) {
          debugWarn('Authentication error when saving goal date');
          this.logout();
          return { success: false, error: 'Authentication error' };
        }

        // Handle API errors
        if (statusCode?.value && statusCode.value >= 400) {
          debugWarn(`API error ${statusCode.value} when saving goal date`);
          return { success: false, error: `API error: ${statusCode.value}` };
        }

        if (!data.value) {
          debugWarn('No data returned from goal date API', data);
          return { success: false, error: 'No data returned from API' };
        }

        debugLog('✅ Goal date API response:', data.value);
        
        // Update local cache with the saved date. The dedicated backend table is
        // the source of truth; legacy fields stay here only as UI fallback.
        const savedDate = data.value?.date || data.value?.target_date || data.value?.goal_date || date;
        const fieldName = `planWhen${goalCategoryId}`;
        if (!this.profile.values) {
          this.profile.values = {};
        }
        this.profile.values[fieldName] = savedDate;
        this.profile.values[`goalDate${goalCategoryId}`] = savedDate;
        
        return { success: true, data: data.value };
        
      } catch (error: any) {
        debugWarn('Error saving goal date:', error);
        return { success: false, error: error.message || 'Unknown error' };
      }
    },

    // Optimized setProfileValue method with immediate local updates
    async setProfileValue(name: string, value: string) {
      // Early safety check for profile
      if (!this.profile?.id) {
        debugWarn('No active profile to save to');
        this.logout();
        return { success: false, error: 'No active profile' };
      }

      // Initialize profile values if they don't exist
      if (!this.profile.values) {
        this.profile.values = {};
      }

      // OPTIMISTIC UPDATE: Set the value immediately for instant UI feedback
      const context = this.getProfileRequestContext();
      const valueToSave = value;
      const requestSequence = ++profileSaveSequence;
      const saveTimestamp = requestSequence;
      const clientRequestId = createClientRequestId(this.profile.id, name, Date.now());
      valueTimestamps[name] = saveTimestamp;
      this.profile.values[name] = valueToSave;
      const entry = this.queueAnswerForOutbox(name, valueToSave, clientRequestId, context);

      try {
        // Make API call. Retried on 5xx errors: the backend answer
        // receipts make replays with the same client_request_id idempotent.
        const { data, statusCode } = await this.sendOutboxEntry(entry, context);

        if (!this.isProfileRequestCurrent(context)) {
          return { success: false, queued: true, stale: true };
        }

        // Handle authentication errors
        if (statusCode?.value === 401) {
          debugWarn('Authentication error when saving profile value');
          this.logout(true);
          return { success: false, error: 'Authentication error' };
        }

        // Handle API errors
        if (statusCode?.value && statusCode.value >= 400) {
          debugWarn(
            `API error ${statusCode.value} when saving profile value`,
          );
          if (statusCode.value >= 500 || statusCode.value === 429 || statusCode.value === 408) {
            // Server hiccup: keep the answer queued so it is delivered later.
            return { success: false, queued: true, error: `API error: ${statusCode.value}` };
          }
          this.acknowledgeOutboxEntry(entry, context);
          return { success: false, error: `API error: ${statusCode.value}` };
        }

        // Handle successful response
        if (!data.value || !statusCode?.value || statusCode.value < 200 || statusCode.value >= 300) {
          // Network failure / timeout: queue the answer for background delivery.
          debugWarn('No data returned from API - queueing answer for retry', data);
          return { success: false, queued: true, error: 'No data returned from API' };
        }

        debugLog('API Response:', data.value);
        debugLog('🔍 Full profile_values array:', data.value.profile_values);

        // If a newer save was dispatched while this request was in flight,
        // this response's server-computed state (factors/risk values) is
        // stale. Individual values keep their own per-field timestamp
        // protection below; everything else is skipped.
        const responseIsLatest = requestSequence === profileSaveSequence &&
          !this.readAnswerOutbox().some(pending => !sameOutboxEntry(pending, entry));
        if (!responseIsLatest) {
          debugLog(
            `⚠️ Response for ${name} is stale (a newer save is in flight) - skipping factor updates`,
          );
        }

        if (responseIsLatest && data.value.profile_answer_revision !== undefined) {
          (this.profile as any).answer_revision = data.value.profile_answer_revision;
        }

        // Process profile values from API response
        // CRITICAL: Only update values that haven't been modified locally since this API call started
        // AND never overwrite a non-empty local value with null/empty from API
        if (
          responseIsLatest && data.value.profile_values &&
          Array.isArray(data.value.profile_values)
        ) {
          for (const profileValue of data.value.profile_values) {
            if (profileValue.name && profileValue.value !== undefined) {
              const fieldName = profileValue.name;
              const lastLocalUpdate = valueTimestamps[fieldName] || 0;
              const currentLocalValue = this.profile.values[fieldName];
              const apiValue = profileValue.value;
              
              // Skip if this field was updated locally AFTER this API call started
              // This prevents stale API responses from overwriting newer local values
              if (lastLocalUpdate > saveTimestamp || this.readAnswerOutbox().some(
                pending => (pending.kind ?? 'value') === 'value' && pending.name === fieldName,
              )) {
                debugLog(
                  `⚠️ Skipping stale update for ${fieldName} (local update at ${lastLocalUpdate} is newer than API call at ${saveTimestamp})`,
                );
                continue;
              }
              
              // Skip the field we just saved (already handled above)
              if (fieldName === name) {
                debugLog(
                  `Skipping update for ${fieldName} (just saved as ${valueToSave})`,
                );
                continue;
              }
              
              // CRITICAL: Never overwrite a non-empty local value with null/empty from API
              // This prevents the "vanishing answer" bug where API returns stale null values
              const localHasValue = currentLocalValue !== null && currentLocalValue !== undefined && currentLocalValue !== '';
              const apiHasValue = apiValue !== null && apiValue !== undefined && apiValue !== '';
              
              if (localHasValue && !apiHasValue) {
                debugLog(
                  `⚠️ Protecting local value for ${fieldName}: keeping "${currentLocalValue}" instead of API's "${apiValue}"`,
                );
                continue;
              }
              
              // Safe to update - no newer local changes and not overwriting with empty
              this.profile.values[fieldName] = apiValue;
              debugLog(
                `Updated profile value: ${fieldName} = ${apiValue}`,
              );
            }
          }
        }

        // Initialize factors if they don't exist
        if (!this.profile.factors) {
          this.profile.factors = {};
        }

        // Update factors from response
        if (
          responseIsLatest &&
          data.value.profile_factors &&
          Array.isArray(data.value.profile_factors)
        ) {
          for (const factor of data.value.profile_factors) {
            const {
              category_id,
              improve,
              status,
              value: factorValue,
              label,
              risk_value,
            } = factor;

            if (
              category_id &&
              (factorValue !== null || factorValue !== undefined)
            ) {
              // Keep an improve choice made locally after this API call
              // started - the response carries a stale improve flag.
              const localImproveIsNewer =
                improveTimestamps[category_id] !== undefined &&
                (improveTimestamps[category_id] > saveTimestamp || this.readAnswerOutbox().some(
                  pending => pending.kind === 'improve' && pending.category === category_id,
                ));

              this.profile.factors[category_id] = {
                improve: localImproveIsNewer
                  ? this.profile.factors[category_id]?.improve ?? (improve || false)
                  : (improve || false),
                status: status?.replace('profile.', '') || '',
                value: factorValue,
                label: label || '',
                risk_value: risk_value,
              };
              debugLog(
                `Updated factor ${category_id}: value=${factorValue}, risk_value=${risk_value}`,
              );
            }
          }
        }

        // Handle single factor update
        if (responseIsLatest && data.value.category_id && data.value.risk_status_name) {
          const singleFactorImproveIsNewer =
            improveTimestamps[data.value.category_id] !== undefined &&
            (improveTimestamps[data.value.category_id] > saveTimestamp || this.readAnswerOutbox().some(
              pending => pending.kind === 'improve' && pending.category === data.value.category_id,
            ));

          this.profile.factors[data.value.category_id] = {
            label: data.value.risk_status_label || '',
            status: data.value.risk_status_name?.replace('profile.', '') || '',
            value: data.value.value,
            risk_value: data.value.risk_value,
            improve: singleFactorImproveIsNewer
              ? this.profile.factors[data.value.category_id]?.improve ??
                (data.value.improve || false)
              : (data.value.improve || false),
          };
          debugLog(`Updated single factor ${data.value.category_id}`);
        }

        // Handle other response data
        if (responseIsLatest && data.value.fitness_text !== undefined) {
          this.profile.fitness_text = data.value.fitness_text;
        }

        if (responseIsLatest && 'energy_balance_value' in data.value) {
          if (name !== 'foodEnergyIntake') {
            this.profile.values['foodEnergyIntake'] =
              data.value.energy_intake_value;
          }
          if (name !== 'foodEnergyBalance') {
            this.profile.values['foodEnergyBalance'] =
              data.value.energy_balance_value;
          }
        }

        if (responseIsLatest && data.value.physical_activity_factor) {
          const paf = data.value.physical_activity_factor;
          this.profile.factors[paf.category_id] = {
            improve: paf.improve || false,
            label: paf.label || '',
            status: paf.status ? paf.status.replace('profile.', '') : '',
            value: paf.value,
            risk_value: paf.value,
          };
        }

        this.acknowledgeOutboxEntry(entry, context);

        debugLog(`Successfully saved ${name}: ${this.profile.values[name]}`);

        // We are clearly online: deliver anything still waiting in the outbox.
        this.flushAnswerOutbox();

        return { success: true, data: data.value };
      } catch (e: any) {
        debugWarn('Error saving profile value:', e);

        return { success: false, queued: true, error: e.message || 'Unknown error' };
      }
    },

    /**
     * POST an answer with automatic retries. Replays reuse the same
     * client_request_id, which the backend deduplicates via
     * profile_answer_write_receipts.
     */
    async postAnswerWithRetry(
      profileId: number,
      payload: { name: string; value: string; client_request_id: string },
      context?: ProfileRequestContext,
      background = false,
    ) {
      return this.postJsonWithRetry(`/profile/${profileId}/value`, payload, context, background);
    },

    /**
     * Retry server failures briefly. Unknown connectivity and rate limits go
     * back to the durable outbox; background delivery makes one attempt only.
     */
    async postJsonWithRetry(
      url: string,
      payload: Record<string, any>,
      context?: ProfileRequestContext,
      background = false,
    ) {
      context ??= this.getProfileRequestContext();
      const delaysMs = background ? [0] : [0, 800, 2500];
      let last: any = { data: { value: null }, statusCode: { value: null } };

      for (let attempt = 0; attempt < delaysMs.length; attempt++) {
        if (!this.isProfileRequestCurrent(context) || !context.token || answerDeliveryPaused(background)) break;
        if (delaysMs[attempt] > 0) {
          await new Promise((resolve) => setTimeout(resolve, delaysMs[attempt]));
        }
        if (!this.isProfileRequestCurrent(context) || answerDeliveryPaused(background)) break;

        try {
          const { data, statusCode } = await useBackendFetch(url, this.profileRequestOptions(context))
            .post(payload)
            .json();

          last = { data, statusCode };
        } catch {
          break;
        }

        const status = last.statusCode?.value ?? null;

        // Definitive server answer (2xx-4xx): stop retrying.
        if (status === null || status < 500) {
          break;
        }
        // Only 5xx responses are retried here.
      }

      return last;
    },

    // ------------------------------------------------------------------
    // Offline outbox: answers that could not be delivered are persisted
    // per profile in localStorage and re-sent when connectivity returns
    // (see main.ts 'online' listener) or after the next successful save.
    // ------------------------------------------------------------------

    answerOutboxKey(context?: ProfileRequestContext) {
      return outboxStorageKey(context ?? this.getProfileRequestContext());
    },

    readAnswerOutbox(context?: ProfileRequestContext): OutboxEntry[] {
      return readScopedAnswerOutbox(context ?? this.getProfileRequestContext());
    },

    writeAnswerOutbox(entries: OutboxEntry[], context?: ProfileRequestContext) {
      context ??= this.getProfileRequestContext();
      const key = this.answerOutboxKey(context);
      if (!key) return;
      try {
        if (entries.length === 0) {
          localStorage.removeItem(key);
        } else {
          localStorage.setItem(key, JSON.stringify(entries));
        }
        localStorage.removeItem(this.profileScopedStorageKey('answer-outbox', context.profileId));
        outboxMemoryFallback.delete(key);
      } catch (e) {
        // Storage may be full/disabled; retain this session's edits and still try delivery.
        outboxMemoryFallback.set(key, entries);
        debugWarn('Could not persist answer outbox', e);
      }
      this.answerSyncVersion++;
      profileSnapshotSequence++;
    },

    queueAnswerForOutbox(name: string, value: string, clientRequestId: string, context?: ProfileRequestContext): OutboxEntry {
      context ??= this.getProfileRequestContext();
      // Keep only the latest value per field.
      const entries = this.readAnswerOutbox(context).filter(
        (entry) => !((entry.kind ?? 'value') === 'value' && entry.name === name),
      );
      const entry: OutboxEntry = {
        kind: 'value',
        name,
        value,
        client_request_id: clientRequestId,
        ts: Date.now(),
      };
      entries.push(entry);
      this.writeAnswerOutbox(entries, context);
      debugLog(
        `Queued answer for later delivery: ${name} (${entries.length} in outbox)`,
      );
      return entry;
    },

    queueImproveForOutbox(category: number, value: string, context?: ProfileRequestContext): OutboxEntry {
      context ??= this.getProfileRequestContext();
      // Keep only the latest improve choice per category.
      const entries = this.readAnswerOutbox(context).filter(
        (entry) => !(entry.kind === 'improve' && entry.category === category),
      );
      const entry: OutboxEntry = {
        kind: 'improve',
        name: `improve:${category}`,
        value,
        category,
        client_request_id: createClientRequestId(context.profileId!, `improve:${category}`, Date.now()),
        ts: Date.now(),
      };
      entries.push(entry);
      this.writeAnswerOutbox(entries, context);
      debugLog(
        `Queued improve choice for later delivery: category ${category} (${entries.length} in outbox)`,
      );
      return entry;
    },

    async flushAnswerOutbox(): Promise<void> {
      if (!this.loggedIn || !this.token || !this.profile?.id || answerDeliveryPaused(true)) return;
      const context = this.getProfileRequestContext();
      const scope = requestScope(context);
      const existing = outboxFlushRequests.get(scope);
      if (existing) return existing;

      const request = (async () => {
        // Re-read after every await: new edits must never be replaced by a captured array.
        while (this.isProfileRequestCurrent(context) && !answerDeliveryPaused(true)) {
          const entry = this.readAnswerOutbox(context).find(candidate =>
            !answerWriteRequests.has(`${outboxStorageKey(context)}:${answerField(candidate)}`),
          );
          if (!entry) break;
          const response = await this.sendOutboxEntry(entry, context, true);
          if (!this.isProfileRequestCurrent(context)) break;
          const status = response.statusCode?.value ?? null;
          if (status === 401) {
            this.logout(true);
            break;
          }
          if (status === null || status < 200 || status >= 500 || status === 429 || status === 408 ||
            (status < 300 && !response.data?.value)) break;
          if (status >= 400) debugWarn(`Outbox entry rejected (${status}): ${entry.name}`);
          if (status >= 300 && status < 400) break;
          this.acknowledgeOutboxEntry(entry, context);
        }
      })();
      outboxFlushRequests.set(scope, request);
      try {
        await request;
      } catch (error) {
        debugWarn('Outbox delivery paused', error);
      } finally {
        if (outboxFlushRequests.get(scope) === request) outboxFlushRequests.delete(scope);
      }
    },
    acknowledgeOutboxEntry(entry: OutboxEntry, context: ProfileRequestContext) {
      if (!this.isProfileRequestCurrent(context)) return;
      const entries = this.readAnswerOutbox(context);
      const remaining = entries.filter(candidate => !sameOutboxEntry(candidate, entry));
      if (remaining.length !== entries.length) {
        this.writeAnswerOutbox(remaining, context);
      }
    },
    async sendOutboxEntry(entry: OutboxEntry, context: ProfileRequestContext, background = false) {
      const key = `${outboxStorageKey(context)}:${answerField(entry)}`;
      const previous = answerWriteRequests.get(key);
      const request = (async () => {
        // Serialize the same account/profile field even if its session token rotates.
        if (previous) await previous;
        const empty = { data: { value: null }, statusCode: { value: null } };
        if (!this.isProfileRequestCurrent(context) || !context.profileId ||
          !this.readAnswerOutbox(context).some(candidate => sameOutboxEntry(candidate, entry))) return empty;
        if (entry.kind === 'improve' && entry.category !== undefined) {
          return this.postJsonWithRetry(`/profile/${context.profileId}/improve`, {
            category_id: entry.category,
            value: entry.value,
          }, context, background);
        }
        return this.postAnswerWithRetry(context.profileId, {
          name: entry.name,
          value: entry.value,
          client_request_id: entry.client_request_id ?? `legacy:${context.profileId}:${entry.name}:${entry.ts}`.slice(0, 120),
        }, context, background);
      })();
      answerWriteRequests.set(key, request);
      this.answerSyncVersion++;
      try {
        return await request;
      } finally {
        if (answerWriteRequests.get(key) === request) answerWriteRequests.delete(key);
        this.answerSyncVersion++;
      }
    },
    getPendingAnswerSaveCount(): number {
      return countPendingAnswerSaves(this.getProfileRequestContext());
    },
    async whenAnswerSavesSettled(): Promise<boolean> {
      const context = this.getProfileRequestContext();
      const prefix = `${outboxStorageKey(context)}:`;
      while (this.isProfileRequestCurrent(context)) {
        const requests = [...answerWriteRequests].filter(([key]) => key.startsWith(prefix)).map(([, request]) => request);
        if (!requests.length) return this.pendingAnswerSaves === 0;
        await Promise.allSettled(requests);
      }
      return false;
    },
    async refreshCurrentProfileSafely(): Promise<boolean> {
      const context = this.getProfileRequestContext();
      if (!this.loggedIn || !this.token || answerDeliveryPaused(true)) return false;
      await this.whenAnswerSavesSettled();
      if (!this.isProfileRequestCurrent(context) || answerDeliveryPaused(true)) return false;
      await this.flushAnswerOutbox();
      await this.whenAnswerSavesSettled();
      if (!this.isProfileRequestCurrent(context) || answerDeliveryPaused(true) || this.pendingAnswerSaves > 0) return false;
      return this.reconcileCurrentProfile(true);
    },
    async setImproveValue(category: number, value: string) {
      if (!this.profile?.id) {
        this.logout();
        return null;
      }

      // OPTIMISTIC: reflect the choice immediately (no waiting for the
      // network round-trip) and remember it was chosen on this device so
      // stale server snapshots cannot revert it.
      const context = this.getProfileRequestContext();
      improveTimestamps[category] = ++profileSaveSequence;

      if (!this.profile.factors) {
        this.profile.factors = {};
      }
      if (this.profile.factors[category]) {
        this.profile.factors[category].improve = value;
      }

      const entry = this.queueImproveForOutbox(category, value, context);
      const { data, statusCode } = await this.sendOutboxEntry(entry, context);

      if (!this.isProfileRequestCurrent(context)) return false;

      if (statusCode?.value === 401) {
        this.logout(true);
        return null;
      }

      const status = statusCode?.value ?? null;

      if (status === null || status >= 500 || status === 429 || status === 408) {
        // Offline or server down: keep the local choice and deliver it later.
        return false;
      }

      const saved = status >= 200 && status < 300 && data.value === 'Saved!';
      if (saved || status >= 400) this.acknowledgeOutboxEntry(entry, context);
      return saved;
    },
    async fetchSatisfaction() {
      if (this.profile?.id === null) {
        this.logout();
        return null;
      }

      const { data, statusCode } = await useBackendFetch(
        `/profile/${this.profile.id}/satisfied`,
      )
        .get()
        .json();

      if (statusCode?.value === 401) {
        this.logout();
        return null;
      }

      return data.value.satisfied;
    },
    async updateSatisfaction(categoryIds: string[]) {
      if (this.profile?.id === null) {
        this.logout();
        return null;
      }

      const { data, statusCode } = await useBackendFetch(
        `/profile/${this.profile.id}/satisfied`,
      )
        .post({
          category_ids: categoryIds,
        })
        .json();

      if (statusCode?.value === 401) {
        this.logout();
        return null;
      }

      return data.value;
    },
    async fetchGoals() {
      if (this.profile?.id === null) {
        this.logout();
        return null;
      }

      debugLog('Fetching goals for profile:', this.profile.id);
      const { data, statusCode } = await useBackendFetch(
        `/profile/${this.profile.id}/goals`,
      )
        .get()
        .json();

      if (statusCode?.value === 401) {
        this.logout();
        return null;
      }

      debugLog('Goals API full response:', data);
      debugLog('Goals API value:', data.value);
      debugLog('Goals array:', data.value?.goals);
      return data.value?.goals || [];
    },
    async updateGoals(goals: { category_id: number; vision: number; target_date?: string }[]) {
      if (this.profile?.id === null) {
        this.logout();
        return null;
      }

      const { data, statusCode } = await useBackendFetch(
        `/profile/${this.profile.id}/goals`,
      )
        .post({ goals })
        .json();

      if (statusCode?.value === 401) {
        this.logout();
        return null;
      }

      return data.value;
    },
    async fetchFactorsForGoal(goalId: number) {
      // This is a placeholder. We will need a new backend endpoint for this.
      debugLog('Fetching factors for goal:', goalId);
      return Promise.resolve([]);
    },
    async updateGoalPlan(goalId: number, planText: string, selectedFactors: { category_id: number; vision: number }[]) {
      if (!this.profile?.id) {
        this.logout();
        return null;
      }

      const { data, statusCode } = await useBackendFetch(
        `/profile/${this.profile.id}/plan/${goalId}`,
      )
        .post({
          action_plan: planText,
          selected_factors: selectedFactors
        })
        .json();

      if (statusCode?.value === 401) {
        this.logout();
        return null;
      }

      return data.value;
    },
    async updateGoalFactors(goalId: number, factorIds: number[]) {
      if (!this.profile?.id) {
        this.logout();
        return null;
      }

      const { data, statusCode } = await useBackendFetch(
        `/profile/${this.profile.id}/plan/${goalId}/factors`,
      )
        .post({ factor_ids: factorIds })
        .json();

      if (statusCode?.value === 401) {
        this.logout();
        return null;
      }

      return data.value;
    },
    async fetchPlannedGoals() {
      if (this.profile?.id === null) {
        this.logout();
        return null;
      }

      const { data, statusCode } = await useBackendFetch(
        `/profile/${this.profile.id}/plan`,
      )
        .get()
        .json();

      if (statusCode?.value === 401) {
        this.logout();
        return null;
      }

      return data.value.goals;
    },
    async updatePlannedGoals(plan: Record<number, GoalPlan>[]) {
      if (this.profile?.id === null) {
        this.logout();
        return null;
      }

      const { data, statusCode } = await useBackendFetch(
        `/profile/${this.profile.id}/plan`,
      )
        .post({ plan })
        .json();

      if (statusCode?.value === 401) {
        this.logout();
        return null;
      }

      return data.value;
    },
    async fetchPlan() {
      if (!this.profile?.id) {
        this.logout();
        return null;
      }

      debugLog('Fetching plan for profile:', this.profile.id);

      // Try finished plan first
      let { data, statusCode } = await useBackendFetch(
        `/profile/${this.profile.id}/plan?finished=true`,
      )
        .get()
        .json();

      // If nothing returned, fallback to current (not finished) plan
      if (!data.value?.goals || data.value.goals.length === 0) {
        debugLog('No finished plan found. Falling back to current plan.');
        const fallback = await useBackendFetch(
          `/profile/${this.profile.id}/plan`,
        )
          .get()
          .json();
        data = fallback.data;
        statusCode = fallback.statusCode;
      }

      debugLog('Plan API response:', data.value);

      if (statusCode?.value === 401) {
        this.logout();
        return null;
      }

      // Process the plan data to include all necessary information
      if (data.value?.goals) {
        debugLog('Processing goals:', data.value.goals);
        // Fallback: merge dates from planned (non-finished) goals if missing in finished plan payload
        try {
          const plannedGoals = await this.fetchPlannedGoals();
          if (Array.isArray(plannedGoals) && plannedGoals.length) {
            const idToDate: Record<number, any> = {};
            for (const g of plannedGoals as any[]) {
              idToDate[g.category_id] = g.date || g.target_date || g.goal_date || g.due_date || null;
            }
            for (const goal of data.value.goals as any[]) {
              if (!goal.date) {
                const d = idToDate[goal.category_id];
                if (d) goal.date = typeof d === 'string' && d.includes('T') ? d.split('T')[0] : d;
              }
            }
          }
        } catch (e) {
          // non-fatal
        }
        // Fetch answers for all goals and their factors
        await Promise.all(data.value.goals.map(async (goal: any) => {
          // Normalize goal date property so UI components can render it
          if (!goal.date) {
            goal.date = goal.target_date || goal.goal_date || goal.due_date || goal?.date || null;
          }
          // If date is an ISO string with time, trim to YYYY-MM-DD
          if (typeof goal.date === 'string' && goal.date.includes('T')) {
            goal.date = goal.date.split('T')[0];
          }
          // Fetch answers for the main goal
          const goalAnswers = await this.fetchCategoryAnswers(goal.category_id);
          goal.answers = goalAnswers;
          debugLog(`Answers for goal ${goal.category_id}:`, goalAnswers);

          // Fetch answers for all factors
          if (goal.factors) {
            debugLog(`Processing factors for goal ${goal.category_id}:`, goal.factors);
            await Promise.all(goal.factors.map(async (factor: any) => {
              const factorAnswers = await this.fetchCategoryAnswers(factor.category_id);
              factor.answers = factorAnswers;
              debugLog(`Answers for factor ${factor.category_id}:`, factorAnswers);
            }));
          }
        }));
      }

      debugLog('Returning processed plan data:', data.value);
      return data.value;
    },
    async fetchPlanById(planId: number) {
      const { data, statusCode } = await useBackendFetch(
        `/profile/${planId}/plan?finished=true`,
      )
        .get()
        .json();

      if (statusCode?.value === 401) {
        this.logout();
        return null;
      }

      // Normalize goal date for historic plans as well
      if (data.value?.goals) {
        for (const goal of data.value.goals) {
          if (!goal.date) {
            goal.date = goal.target_date || goal.goal_date || goal.due_date || goal?.date || null;
          }
          if (typeof goal.date === 'string' && goal.date.includes('T')) {
            goal.date = goal.date.split('T')[0];
          }
        }
      }

      return data.value;
    },
    async emailPlanSummary(profileId: number) {
      const { data, statusCode } = await useBackendFetch(
        `/profile/${profileId}/plan/email-summary`,
      )
        .post({ app_url: window.location.origin })
        .json();

      if (statusCode?.value === 401) {
        this.logout();
        return { success: false, error: 'Authentication error' };
      }

      if (statusCode?.value && statusCode.value >= 400) {
        return { success: false, error: `API error: ${statusCode.value}` };
      }

      return { success: true, data: data.value };
    },
    async getNewPlan() {
      await this.reconcileCurrentProfile(true);

      if (this.profile?.id) {
        return;
      }

      const { data, statusCode } = await useBackendFetch(`/profile/create`)
        .post()
        .json();

      if (statusCode?.value === 401) {
        this.logout();
        return;
      }

      this.profile = data.value.profile;
      clearValueTimestamps();
      this.clearProfileScopedSavePoints(this.profile?.id);
      this.normalizeProfileScopedState();
    },
    async finishPlan() {
      if (!this.profile?.id) {
        return;
      }

      const finishedProfileId = this.profile.id;

      const { data, statusCode } = await useBackendFetch(`/profile/finish`)
        .post()
        .json();

      if (statusCode?.value === 401) {
        this.logout();
        return;
      }

      this.profiles = [
        {
          id: this.profile.id,
          created_at: this.profile.created_at,
        },
        ...this.profiles,
      ];

      this.clearProfileScopedSavePoints(finishedProfileId);
      this.profile = null;
      this.feedbackQuestions = [];
    },
    async fetchFeedbackQuestions() {
      if (this.feedbackQuestions && this.feedbackQuestions.length) {
        return;
      }

      const { data, statusCode } = await useBackendFetch(`/feedback`)
        .get()
        .json();

      if (statusCode?.value === 401) {
        this.logout();
        return;
      }

      this.feedbackQuestions = data.value;
    },
    async saveFeedback(answers: { [key: string]: number | string }, profileId?: number) {
      const targetProfileId = profileId || this.profile?.id;

      if (!targetProfileId) {
        debugWarn('Cannot save feedback: No active profile');
        return { success: false, error: 'No active profile' };
      }

      try {
        // Convert all numeric values to strings to match the backend expectation
        const stringAnswers: { [key: string]: string } = {};
        Object.keys(answers).forEach(key => {
          // Convert all answers to strings as required by your CreateFeedbackRequest
          stringAnswers[key] = String(answers[key]);
        });

        debugLog('Sending feedback with answers:', stringAnswers);

        const { data, statusCode, error } = await useBackendFetch(
          `/profile/${targetProfileId}/feedback`,
        )
          .post({
            answers: stringAnswers,
          })
          .json();

        debugLog('Feedback save response:', {
          data: data.value,
          statusCode: statusCode.value,
          error: error?.value,
        });

        if (statusCode?.value === 401) {
          debugWarn('Authentication error when saving feedback');
          this.logout();
          return { success: false, error: 'Authentication error' };
        }

        // Handle 403 specifically - this means feedback was already submitted
        if (statusCode?.value === 403) {
          debugWarn('Feedback was already submitted for this profile');
          await this.markFinalFeedbackSubmitted();
          return {
            success: false,
            error: 'Forbidden',
            alreadySubmitted: true,
            message: data.value?.message || 'This action is unauthorized.',
          };
        }

        if (statusCode?.value && statusCode.value >= 400) {
          debugWarn(
            'Error saving feedback, status code:',
            statusCode.value,
          );

          // Handle validation errors
          if (statusCode.value === 422 && data.value && data.value.errors) {
            debugWarn('Validation errors:', data.value.errors);
            return {
              success: false,
              error: 'Validation error',
              details: data.value.errors,
            };
          }

          return { success: false, error: `API error: ${statusCode.value}` };
        }

        await this.markFinalFeedbackSubmitted();

        return {
          success: true,
          data: data.value || {},
        };
      } catch (e: any) {
        debugWarn('Exception when saving feedback:', e);
        return { success: false, error: e.message || 'Unknown error' };
      }
    },
    async fetchAnswerTexts(categoryId: number) {
      if (!this.profile?.id) {
        this.logout();
        return null;
      }

      const { data, statusCode } = await useBackendFetch(
        `/answers/${categoryId}`,
      )
        .get()
        .json();

      if (statusCode?.value === 401) {
        this.logout();
        return null;
      }

      if (data.value?.answers) {
        this.answerTexts[categoryId] = data.value.answers;
      }

      return data.value?.answers || null;
    },

    getAnswerText(categoryId: number, value: number): string | null {
      const answers = this.answerTexts[categoryId];
      if (!answers) return null;

      const answer = answers.find(a => String(a.value) === String(value));
      return answer?.text || null;
    },
    async fetchCategoryAnswers(categoryId: number): Promise<Record<string, string>> {
      const cached = this.categoryAnswers[categoryId];
      if (cached && Object.keys(cached).length > 0) {
        return cached;
      }

      if (categoryAnswerRequests[categoryId]) {
        return categoryAnswerRequests[categoryId];
      }

      categoryAnswerRequests[categoryId] = (async () => {
        const { data, statusCode } = await useBackendFetch(`/categories/${categoryId}/answers`)
          .get()
          .json();

        if (statusCode.value === 401) {
          this.logout();
          return {};
        }

        if ((statusCode.value || 0) >= 400) {
          debugWarn(`Failed to fetch category answers for ${categoryId}:`, statusCode.value);
          return this.categoryAnswers[categoryId] || {};
        }

        if (data.value?.answers) {
          this.categoryAnswers[categoryId] = data.value.answers;
          return data.value.answers;
        }

        return this.categoryAnswers[categoryId] || {};
      })().finally(() => {
        delete categoryAnswerRequests[categoryId];
      });

      return categoryAnswerRequests[categoryId];
    },

    // Session management methods
    updateLastActivity() {
      this.lastActivity = Date.now();
    },

    isSessionExpired() {
      if (!this.loggedIn) {
        return true;
      }
      
      // If no lastActivity recorded, set it to now (for existing logged-in users)
      if (!this.lastActivity || this.lastActivity === 0) {
        this.updateLastActivity();
        return false;
      }
      
      const now = Date.now();
      const timeSinceLastActivity = now - this.lastActivity;
      return timeSinceLastActivity > this.sessionTimeout;
    },

    getTimeUntilExpiry() {
      if (!this.loggedIn) {
        return 0;
      }
      
      // If no lastActivity recorded, set it to now
      if (!this.lastActivity || this.lastActivity === 0) {
        this.updateLastActivity();
        return this.sessionTimeout;
      }
      
      const now = Date.now();
      const timeSinceLastActivity = now - this.lastActivity;
      const timeUntilExpiry = this.sessionTimeout - timeSinceLastActivity;
      return Math.max(0, timeUntilExpiry);
    },

    async validateSession() {
      if (!this.loggedIn || !this.token) {
        return false;
      }

      try {
        // Make a simple API call to validate the token
        const { statusCode } = await useBackendFetch('/user/profile')
          .get()
          .json();

        if (statusCode.value === 401) {
          debugLog('Session expired - token invalid');
          this.logout();
          return false;
        }

        if (statusCode.value === 200) {
          this.updateLastActivity();
          return true;
        }

        return false;
      } catch (error) {
        debugWarn('Session validation error:', error);
        return false;
      }
    },

    async checkAndHandleSessionExpiry() {
      if (!this.loggedIn) {
        return;
      }

      // Check if session is expired based on time
      if (this.isSessionExpired()) {
        debugLog('Session expired due to inactivity');
        this.logout();
        return;
      }

      // Check if we need to validate with backend
      const timeUntilExpiry = this.getTimeUntilExpiry();
      const warningTime = 15 * 60 * 1000; // 15 minutes before expiry

      if (timeUntilExpiry <= warningTime && !this.sessionWarningShown) {
        // Show warning to user
        this.showSessionWarning();
        this.sessionWarningShown = true;
      }

      // Only validate with backend if very close to expiry (2 minutes) to reduce API calls
      if (timeUntilExpiry <= 2 * 60 * 1000) { // 2 minutes before expiry
        await this.validateSession();
      }
    },

    showSessionWarning() {
      // This will be implemented in the UI component
      debugLog('Session will expire soon. Please save your work.');
    },
  },
});
